/** Query keys written by policy list `useListQueryState` + `serializePoliciesFilters`. */
import { formatDateOnlyForPicker } from './dateOnly';
import {
  EMPTY_POLICY_ISSUED_DATE_RANGE_FILTER,
  arePolicyIssuedDateRangeFiltersEqual,
  normalizePolicyIssuedDateRangeFilter,
  resolvePolicyIssuedDateRangeForApi,
} from './policyIssuedDateRangeFilter';

export const POLICY_LIST_URL_PARAM_KEYS = [
  'search',
  'page',
  'pageSize',
  'sort',
  'appType',
  'issuedDatePreset',
  'issuedDateStart',
  'issuedDateEnd',
  'coverageStatus',
  'leaseStart',
  'leaseEnd',
  'tenantCount',
  'activeLease',
  'expiringRange',
  'leaseBucket',
];

/** URL token for Unknown app type (empty string cannot round-trip alone). */
export const POLICY_APP_TYPE_UNKNOWN_URL_TOKEN = '__unknown__';

export const parsePolicyAppTypeFilterValues = (raw) => {
  if (raw == null || raw === '') return [];
  return raw.split(',').map((part) => (part === POLICY_APP_TYPE_UNKNOWN_URL_TOKEN ? '' : part));
};

export const serializePolicyAppTypeFilterValues = (values) => {
  if (!Array.isArray(values) || values.length === 0) return '';
  return values
    .map((value) => (value === '' ? POLICY_APP_TYPE_UNKNOWN_URL_TOKEN : value))
    .join(',');
};

/** Values sent to POST /property/policy for app-type filtering. */
export const resolvePolicyAppTypeFilterForApi = (values) => normalizeArrayFilter(values);

export const DEFAULT_POLICY_LIST_EXTRA_FILTERS = {
  selectedAppType: [],
  selectedIssuedDate: { ...EMPTY_POLICY_ISSUED_DATE_RANGE_FILTER },
  selectedCoverageStatus: [],
  selectedLeaseDate: {
    startDate: null,
    endDate: null,
  },
  selectedTenantCount: [],
  activeLease: [],
  selectedExpiringRange: null,
  selectedLeaseBucket: null,
};

function parseIssuedDateRangeFromParams(searchParams, fallbackRange) {
  const preset = searchParams.get('issuedDatePreset') || fallbackRange?.preset || null;
  const startDate = formatDateOnlyForPicker(
    searchParams.get('issuedDateStart') || fallbackRange?.startDate || '',
  );
  const endDate = formatDateOnlyForPicker(
    searchParams.get('issuedDateEnd') || fallbackRange?.endDate || '',
  );

  if (!preset && !startDate && !endDate) {
    return normalizePolicyIssuedDateRangeFilter(fallbackRange);
  }

  if (!preset && (startDate || endDate)) {
    return normalizePolicyIssuedDateRangeFilter({
      preset: 'custom',
      startDate,
      endDate,
    });
  }

  return normalizePolicyIssuedDateRangeFilter({
    preset,
    startDate,
    endDate,
  });
}

export const parsePoliciesFilters = (searchParams, _paramKey, fallback) => ({
  selectedAppType: searchParams.has('appType')
    ? parsePolicyAppTypeFilterValues(searchParams.get('appType'))
    : fallback.selectedAppType,
  selectedIssuedDate: parseIssuedDateRangeFromParams(searchParams, fallback.selectedIssuedDate),
  selectedCoverageStatus: searchParams.get('coverageStatus')
    ? searchParams.get('coverageStatus').split(',').filter(Boolean)
    : fallback.selectedCoverageStatus,
  leaseStart: searchParams.get('leaseStart') || fallback.selectedLeaseDate?.startDate || null,
  leaseEnd: searchParams.get('leaseEnd') || fallback.selectedLeaseDate?.endDate || null,
  selectedTenantCount: searchParams.get('tenantCount')
    ? searchParams.get('tenantCount').split(',').filter(Boolean)
    : fallback.selectedTenantCount,
  activeLease: searchParams.get('activeLease')
    ? searchParams.get('activeLease').split(',').filter(Boolean)
    : fallback.activeLease,
  selectedExpiringRange:
    searchParams.get('expiringRange') || fallback.selectedExpiringRange || null,
  selectedLeaseBucket: searchParams.get('leaseBucket') || fallback.selectedLeaseBucket || null,
});

/** `useListQueryState` parseExtraFilters: nested shape used by policy screens. */
export const parsePoliciesExtraFilters = (searchParams, paramKey, fallback) => {
  const parsed = parsePoliciesFilters(searchParams, paramKey, fallback);
  return {
    selectedAppType: parsed.selectedAppType,
    selectedIssuedDate: parsed.selectedIssuedDate,
    selectedCoverageStatus: parsed.selectedCoverageStatus,
    selectedLeaseDate: {
      startDate: parsed.leaseStart,
      endDate: parsed.leaseEnd,
    },
    selectedTenantCount: parsed.selectedTenantCount,
    activeLease: parsed.activeLease,
    selectedExpiringRange: parsed.selectedExpiringRange,
    selectedLeaseBucket: parsed.selectedLeaseBucket,
  };
};

export const serializePoliciesFilters = (filters) => ({
  appType: serializePolicyAppTypeFilterValues(filters.selectedAppType),
  issuedDatePreset: filters.selectedIssuedDate?.preset || '',
  issuedDateStart:
    filters.selectedIssuedDate?.preset === 'custom'
      ? filters.selectedIssuedDate?.startDate || ''
      : '',
  issuedDateEnd:
    filters.selectedIssuedDate?.preset === 'custom'
      ? filters.selectedIssuedDate?.endDate || ''
      : '',
  coverageStatus:
    Array.isArray(filters.selectedCoverageStatus) && filters.selectedCoverageStatus.length > 0
      ? filters.selectedCoverageStatus.join(',')
      : '',
  leaseStart: filters.selectedLeaseDate?.startDate || '',
  leaseEnd: filters.selectedLeaseDate?.endDate || '',
  tenantCount:
    Array.isArray(filters.selectedTenantCount) && filters.selectedTenantCount.length > 0
      ? filters.selectedTenantCount.join(',')
      : '',
  activeLease:
    Array.isArray(filters.activeLease) && filters.activeLease.length > 0
      ? filters.activeLease.join(',')
      : '',
  expiringRange: filters.selectedExpiringRange || '',
  leaseBucket: filters.selectedLeaseBucket || '',
});

export const areArraysEqual = (a = [], b = []) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

export const areDateRangesEqual = (a = {}, b = {}) =>
  (a?.startDate || null) === (b?.startDate || null) &&
  (a?.endDate || null) === (b?.endDate || null);

export const normalizeArrayFilter = (value) => (Array.isArray(value) ? value : []);

export const normalizeDateRangeFilter = (value) => ({
  startDate: value?.startDate || null,
  endDate: value?.endDate || null,
});

export {
  arePolicyIssuedDateRangeFiltersEqual,
  normalizePolicyIssuedDateRangeFilter,
  resolvePolicyIssuedDateRangeForApi,
};

export function buildPolicyListRequestFilter({
  filter,
  selectedAppType,
  selectedIssuedDate,
  selectedCoverageStatus,
  selectedLeaseDate,
  selectedTenantCount,
  activeLease,
  selectedExpiringRange,
  selectedLeaseBucket,
}) {
  return {
    ...filter,
    searchText: (filter?.searchText || '').trim(),
    selectedAppType,
    selectedIssuedDate: resolvePolicyIssuedDateRangeForApi(selectedIssuedDate),
    selectedCoverageStatus,
    selectedLeaseDate,
    selectedTenantCount,
    activeLease,
    selectedExpiringRange,
    selectedLeaseBucket,
  };
}
